import type { Components, Theme } from '@mui/material/styles';

import type { AvatarLook, TextTone } from './looks';
import { SHAPE, token, WEIGHT } from './scales';

/** The colour role of each text tone. `inherit` takes the colour around the text. */
const TEXT_TONES: [tone: TextTone, color: (theme: Theme) => string][] = [
  ['inherit', () => 'inherit'],
  ['default', (theme) => theme.vars.palette.text.primary],
  ['heading', (theme) => theme.vars.palette.text.heading],
  ['muted', (theme) => theme.vars.palette.text.secondary],
  ['disabled', (theme) => theme.vars.palette.text.disabled],
  ['primary', (theme) => theme.vars.palette.text.brand],
  ['success', (theme) => theme.vars.palette.success.main],
  ['warning', (theme) => theme.vars.palette.warning.dark],
  ['danger', (theme) => theme.vars.palette.error.main],
  ['onMedia', (theme) => theme.vars.palette.primary.contrastText],
];

/** The side in px and the type step of the initials of each avatar size. */
const AVATAR_SIZES: [size: AvatarLook['size'], px: number, text: 'tag' | 'caption' | 'body1' | 'h4'][] = [
  ['sm', 24, 'tag'],
  ['md', 32, 'caption'],
  ['lg', 40, 'body1'],
  ['xl', 64, 'h4'],
];

/** The kit display components: text, avatars, badges, the logo and the editor marks. */
export const DISPLAY: Components<Theme> = {
  KitText: {
    variants: [
      { props: { mono: true }, style: { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' } },
      { props: { faded: true }, style: { opacity: 0.8 } },
      ...TEXT_TONES.map(([tone, color]) => ({
        props: { tone },
        style: ({ theme }: { theme: Theme }) => ({ color: color(theme) }),
      })),
    ],
  },
  MuiAvatar: {
    styleOverrides: {
      root: { fontWeight: WEIGHT.bold },
      colorDefault: ({ theme }) => ({ color: theme.vars.palette.tint.brand.text, backgroundColor: token('brand-100') }),
    },
  },
  KitAvatar: {
    variants: [
      ...AVATAR_SIZES.map(([size, px, text]) => ({
        props: { size },
        style: ({ theme }: { theme: Theme }) => ({ width: px, height: px, fontSize: theme.typography[text].fontSize }),
      })),
      {
        props: { filled: true },
        style: ({ theme }) => ({
          color: theme.vars.palette.primary.contrastText,
          backgroundColor: theme.vars.palette.primary.main,
        }),
      },
    ],
  },
  KitBadge: {
    variants: [{ props: { sentenceCase: true }, style: { textTransform: 'none', letterSpacing: 0 } }],
  },
  KitLogo: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.specimenSmall,
        userSelect: 'none',
        whiteSpace: 'nowrap',
        letterSpacing: '-0.025em',
        fontWeight: WEIGHT.black,
        color: theme.vars.palette.primary.main,
        ...theme.applyStyles('dark', { color: token('brand-200') }),
      }),
    },
    variants: [
      {
        props: { mark: true },
        style: ({ theme }) => ({
          display: 'inline-flex',
          width: 30,
          height: 30,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: SHAPE.small,
          fontSize: theme.typography.subtitle1.fontSize,
          letterSpacing: 0,
          color: theme.vars.palette.primary.contrastText,
          backgroundColor: theme.vars.palette.primary.main,
          ...theme.applyStyles('dark', { color: theme.vars.palette.primary.contrastText }),
        }),
      },
    ],
  },
  KitEditorMark: {
    styleOverrides: { root: { display: 'block' } },
    variants: [
      {
        props: { part: 'marquee' },
        style: { border: `1px solid ${token('brand-600')}`, backgroundColor: token('brand-600', 0.1) },
      },
      { props: { part: 'frame' }, style: ({ theme }) => ({ borderColor: theme.vars.palette.primary.main }) },
      {
        props: { part: 'handle' },
        style: ({ theme }) => ({
          borderRadius: SHAPE.hairline,
          border: `1px solid ${theme.vars.palette.primary.main}`,
          backgroundColor: token('creative-surface'),
        }),
      },
      { props: { part: 'guide' }, style: { backgroundColor: token('playhead') } },
      { props: { part: 'playhead' }, style: { backgroundColor: token('playhead') } },
      { props: { part: 'playhead-head' }, style: { borderRadius: SHAPE.hairline, backgroundColor: token('playhead') } },
      { props: { part: 'safe-band' }, style: { backgroundColor: token('brand-900', 0.45) } },
      { props: { part: 'safe-outline' }, style: { border: `1px dashed ${token('warning-400')}` } },
      { props: { part: 'bar' }, style: { borderRadius: SHAPE.full, backgroundColor: token('brand-300') } },
      { props: { part: 'bar', selected: true }, style: ({ theme }) => ({ backgroundColor: theme.vars.palette.primary.main }) },
      { props: { part: 'keyframe' }, style: ({ theme }) => ({ backgroundColor: theme.vars.palette.primary.main }) },
      { props: { part: 'keyframe-start' }, style: ({ theme }) => ({ backgroundColor: theme.vars.palette.primary.light }) },
      { props: { part: 'underline' }, style: ({ theme }) => ({ backgroundColor: theme.vars.palette.primary.main }) },
    ],
  },
};
